<template>
    <MeterGroup
        :value="computedValue"
        :orientation="orientation"
        :labelOrientation="labelOrientation"
        :size="size"
        :min="min"
        :max="max"
        :class="containerClass"
        :style="containerStyle"
        role="group"
        :aria-label="ariaLabel"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import MeterGroup from 'primevue/metergroup';

type MeterItem = {
    label: string;
    value: number;
    color?: string;
}

type MeterGroupItem = {
    label: string;
    value: number;
    color: string;
}

const props = defineProps({
    modelValue: {
        type: Array as () => MeterItem[],
        default: () => []
    },
    orientation: {
        type: String as () => 'horizontal' | 'vertical',
        default: 'horizontal'
    },
    labelOrientation: {
        type: String as () => 'horizontal' | 'vertical',
        default: 'horizontal'
    },
    size: {
        type: Number,
        default: 40
    },
    min: {
        type: Number,
        default: 0
    },
    max: {
        type: Number,
        default: 100
    },
    ariaLabel: {
        type: String,
        default: 'Meter Group'
    }
});

const computedValue = computed<MeterGroupItem[]>(() => {
    return (props.modelValue || []).map(item => ({
        ...item,
        value: Math.max(props.min, Math.min(props.max, item.value)),
        color: item.color || '#000000' // Provide a default color if none is specified
    }));
});

const containerClass = computed(() => ({
    'p-metergroup': true,
    'p-metergroup-vertical': props.orientation === 'vertical',
    'p-metergroup-label-vertical': props.labelOrientation === 'vertical'
}));

const containerStyle = computed(() => ({
    '--meter-size': `${props.size}px`,
    '--meter-min': props.min,
    '--meter-max': props.max
}));
</script>

<style lang="scss">
.p-metergroup {
    display: flex;
    gap: 1rem;
    
    &-vertical {
        flex-direction: column;
    }

    &-label-vertical {
        .p-metergroup-item {
            flex-direction: column;
            align-items: center;
        }
    }
}

.p-metergroup-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>
